import {applyTransformations,verifyProtectedDomains} from './transforms.js';
import {isStoryTemplate,transformStoryTemplate} from './storyTemplate.js';
import {isVideoReviewTemplate,transformVideoReviewTemplate} from './videoReviewTemplate.js';
import {htmlAttrEscape,htmlTextEscape} from './escaping.js';
import {batchHue} from './styleColors.js';
import {findSupplementTerms,isSupplementCategory} from './leftoverTerms.js';
import {injectCanonicalPatcher,buildCanonicalUrl} from './canonicalPatcher.js';
import {SeededRNG,slugify,buildPageTitle,buildMixedPageTitle} from './random.js';
import {generateAngle,generateSpintaxContent} from './spintaxLlm.js';
import {buildReviewFilenames} from './reviewSlugs.js';
import {derivePageFromSpintax} from './spintaxPageDeriver.js';
import {countSpintaxCombinations,validateSpintax} from './spintaxEngine.js';
import {buildZipBlob} from './zipBuilder.js';
import {crawlPage} from './pageCrawler.js';
import {applyKeywordReplacements,parseOldNames,removeSalesCtas} from './keywordReplacer.js';
import {buildEvidenceContext,evidenceForPrompt,hasEvidence} from './reviewQuality.js';
import {researchProduct,factSheetForPrompt} from './researchLlm.js';

const sleep=(ms)=> new Promise((resolve)=> setTimeout(resolve,ms));

const computeFingerprint=(json)=> [
  json.subtitle || '',json.trailing || '',(json.card_titles || []).join('|'),json.main_video_description || '',
].join('::').toLowerCase().replace(/\s+/g,' ').trim();

export const processBulk=async (fileContent,params,pageCount,addLog)=> {
  const log=(message)=> addLog(message);
  // Pages must point at the owner's real hosting location, never the
  // template's original host.
  if (!params.canonicalBase?.trim()) throw new Error('Canonical Base URL is required: enter the address where these pages will be uploaded.');
  // Three supported templates, detected from the uploaded file.
  const videoReview=isVideoReviewTemplate(fileContent);
  const story=!videoReview && isStoryTemplate(fileContent);
  const templateName=videoReview ? 'Video review (plain HTML)' : story ? 'Story / Next.js' : 'Mitolyn baseline';
  log(`App build: ${__BUILD_TIME__}`);
  log(`Bulk generation: ${pageCount} pages`);
  log(`[Config] Template: ${templateName}`);
  log(`[Config] Canonical base: ${params.canonicalBase || 'default'}`);
  log(`[Config] Video base: ${params.customVideoBase || 'default'}`);
  log(`[Config] Keyword: ${params.altKeyword || '(not set)'}`);
  log(`[Config] Old template name(s): ${parseOldNames(params.oldNames).join(', ') || '(none)'}`);
  const proxyUrl=params.proxyUrl || null;
  // The Keyword is the brand name shown on the page and given to Claude.
  // The JS Redirect File Name (params.product) only names the redirect script.
  const brand=params.altKeyword || params.product;
  log(`[Config] Brand name on page: ${brand} · Redirect script: ${params.product}`);

  // Owner-supplied evidence (screenshots, tests, metrics, video notes...).
  const evidence=buildEvidenceContext(params);
  log(`[Config] Owner evidence: ${hasEvidence(evidence) ? 'supplied' : 'none (pages will say there was no hands-on testing)'}${evidence.videoMoments.length ? ` · ${evidence.videoMoments.length} real video moments` : ''}${evidence.affiliate ? ' · affiliate disclosure on' : ''}`);
  let pageText=null;
  if (params.contextUrl?.trim()) {
    const result=await crawlPage(params.contextUrl);
    if (result.error) log(`⚠ Source crawl failed: ${result.error}`);
    else {
      pageText=result.text;
      log(`✅ Source context: ${result.wordCount} words`);
    }
  }

  // Fact / evidence layer: official site + real user reviews via web search.
  // Runs once per batch; every page is written from the same verified facts.
  let factSheet=null;
  if (params.researchReviews!==false) {
    log('--> Researching the official site and real user reviews (web search)...');
    factSheet=await researchProduct({
      product: brand,keyword: params.keyword,officialUrl: params.contextUrl,pageText,
      apiKey: params.apiKey,model: params.model,proxyUrl,log,
    });
  } else {
    log('[Config] Web research off: writing from the official page only; no user reviews.');
  }
  // Content style: a short YouTube-style video description (default) or the
  // full review article.
  const style=params.contentStyle==='article' ? 'article' : 'description';
  log(`[Config] Content style: ${style==='description' ? 'YouTube video description' : 'Full review article'}`);
  if (style==='description' && !hasEvidence(evidence)) {
    log('   ↳ No evidence fields filled in, so the description makes no hands-on claims. Add your test notes, results, screenshots or video timestamps for a first-hand description.');
  }
  const research={factSheet: factSheetForPrompt(factSheet),evidence: evidenceForPrompt(evidence),style};

  // The SEO title is used exactly as entered (plus a random code per page).
  log(`[Config] SEO title (used as-is): ${params.keyword}`);
  const angle=await generateAngle(
    params.keyword,brand,params.apiKey,params.model,pageText,null,proxyUrl
  );
  const slugRng=new SeededRNG(params.seed);
  // File names: [keyword]-[review phrase]-[6 chars].html, built from the
  // short Keyword (e.g. "SEO NEO"), not the long SEO title. No LLM call.
  const slugList=buildReviewFilenames(brand,pageCount,slugRng);
  log(`[Config] File names like: ${slugList[0]}`);
  const spintaxContent=await generateSpintaxContent(
    params.keyword,brand,angle,params.apiKey,params.model,pageText,null,null,proxyUrl,log,research
  );

  const sample=(spintaxContent.article?.intro || []).join(' ') || spintaxContent.subtitle || '';
  const validity=validateSpintax(sample);
  if (!validity.valid) log(`⚠ Spintax warning: ${validity.error}`);
  log(`✅ Estimated combinations: ${countSpintaxCombinations(sample).toLocaleString()}`);

  // Colour schemes: a random starting hue for the batch, then evenly spread
  // so every page gets its own colours (video-review template).
  const hueStart=new SeededRNG(params.seed!==undefined ? params.seed + 77 : undefined)._next() * 360;

  const zipFiles=[];
  const pageResults=[];
  const fingerprints=new Set();
  let failCount=0;

  for (let i=0;i<pageCount;i++) {
    const filename=slugList[i];
    const canonicalUrl=buildCanonicalUrl(filename,params.canonicalBase);
    const seed=params.seed!==undefined ? params.seed + i * 1000 : Math.floor(Math.random() * 2 ** 32);
    try {
      // Video-review template: "Title (Gfbvj@d#)". Others: "Title [K7Q2MXD9]".
      const titleRng=new SeededRNG(seed + 424242);
      const pageTitle=videoReview ? buildMixedPageTitle(params.keyword,titleRng) : buildPageTitle(params.keyword,titleRng);
      const json=derivePageFromSpintax(
        spintaxContent,params.keyword,brand,new SeededRNG(seed),pageTitle
      );
      // Research + evidence travel with the page so the article builder can
      // add sections only when there's real material for them.
      json.research={factSheet,evidence,officialUrl: params.contextUrl || ''};
      json.colourHue=batchHue(hueStart,i);
      json.siteName=params.siteName || '';
      // Other pages in this batch, for "More videos" links that really exist.
      json.siblings=[1,2].map((step)=> slugList[(i + step) % pageCount])
        .filter((name,n,all)=> name && name!==filename && all.indexOf(name)===n)
        .map((name)=> ({filename: name,url: buildCanonicalUrl(name,params.canonicalBase)}));
      const fingerprint=computeFingerprint(json);
      const unique=!fingerprints.has(fingerprint);
      fingerprints.add(fingerprint);
      const rng=new SeededRNG(seed + 999999);
      const result=videoReview
        ? transformVideoReviewTemplate(
            fileContent,params.keyword,brand,canonicalUrl,json,rng,params.customImageBase,params.customVideoBase,params.scriptBase,params.product
          )
        : story
        ? transformStoryTemplate(
            fileContent,params.keyword,brand,canonicalUrl,json,rng,params.customImageBase,params.customVideoBase,params.scriptBase,params.product
          )
        : applyTransformations(
            fileContent,params.keyword,brand,canonicalUrl,json,rng,params.customImageBase,params.customVideoBase,params.canonicalBase,params.scriptBase,params.product
          );
      const changes=result.changes || [];
      // Replace leftover references to the old product, preserving unrelated alt text.
      const keywordResult=applyKeywordReplacements(result.html,params.altKeyword,params.oldNames);
      changes.push(`[keyword] ${keywordResult.altCount} alt texts and ${keywordResult.nameCount} old-name mentions set to "${params.altKeyword}"`);
      if (i===0) log(`✅ Keyword "${params.altKeyword}": ${keywordResult.altCount} alt texts, ${keywordResult.nameCount} old-name mentions replaced per page`);
      // Strip pushy sales CTAs left in template text (the title is protected).
      const ctaResult=removeSalesCtas(keywordResult.html,brand,[json.seo_title,htmlTextEscape(json.seo_title),htmlAttrEscape(json.seo_title)]);
      changes.push(`[cta] removed ${ctaResult.count} sales call-to-action phrases`);
      if (i===0) log(`✅ Removed ${ctaResult.count} sales call-to-action phrases per page`);
      const html=injectCanonicalPatcher(ctaResult.html,filename,changes,params.canonicalBase);
      // Report supplement-campaign wording left on a non-supplement page.
      if (i===0 && !isSupplementCategory(`${factSheet?.category || ''} ${factSheet?.primary_use_case || ''}`)) {
        const leftovers=findSupplementTerms(html);
        log(leftovers.length
          ? `⚠ Supplement wording still on the page: ${leftovers.map((t)=> `"${t.term}" ×${t.count}`).join(', ')}. Check the template text or the Site Name.`
          : '✅ No supplement wording left on the page');
      }
      const verification=verifyProtectedDomains(fileContent,html);
      if (!verification.ok) throw new Error('Protected domain or fingerprint changed');
      zipFiles.push({filename,content: html});
      pageResults.push({filename,canonicalUrl,changes: changes.length,ok: true,unique,title: json.seo_title,});
      if (i<5 || (i+1)%25===0) log(`✓ ${i+1}/${pageCount}: ${filename}`);
    } catch (error) {
      failCount++;
      log(`✗ ${i+1}/${pageCount}: ${error.message}`);
    }
    if (i%10===0) await sleep(20);
  }

  if (!zipFiles.length) throw new Error('No pages generated. Check the logs and template.');
  const zipBlob=buildZipBlob(zipFiles);
  const zipUrl=URL.createObjectURL(zipBlob);
  const zipFilename=`${slugify(params.keyword)}-bulk-${pageCount}pages.zip`;
  log(`✅ ${zipFiles.length} pages generated;${failCount} failed`);
  return {
    success: true,zipUrl,zipFilename,zipBlob,pageResults,
    successCount: zipFiles.length,failCount,
    totalChanges: pageResults.reduce((total,page)=> total + page.changes,0),
    trueUniqueCount: pageResults.filter((page)=> page.unique).length,
    numBatches: 4,keywordAngles: [angle],angle,
  };
};